<template>
  <!-- This div shows the cookie banner if bannerVisible is true -->
  <div v-if="bannerVisible" class="admin_banner_container" id="tech_admin_banner">
    <div style="width: 100%">
      <!-- v-card used for the cookie banner with a warning color theme -->
      <v-card class="admin_banner_card" variant="flat" color="warning">
        <!-- v-alert shows the actual cookie message with a compact and warning style -->
        <v-alert class="admin_banner_alert" density="compact" type="warning" icon="mdi-alert">
          <span>
            As an admin, can you please give consent that you will uphold our
            <a href="/doc?doc=privacy_policy.htm" class="admin_banner_link" target="_blank"> Privacy Policy </a>
            for any personal data that you access on the system? You can update your consent at any time from your <strong>membership opt-ins</strong> page.
          </span>
          <!-- Button to accept admin -->
          <v-btn color="primary-700" flat class="tech_primary_button accept_admin_button" @click="acceptAdminPrivacyPolicy">Accept</v-btn>
        </v-alert>
      </v-card>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { axiosLevelTech } from '@/services/axiosConfig'; // Import axios for HTTP requests
import { useUserDataStore } from '@/store/userDataStore'; // Import Pinia store

const userDataStore = useUserDataStore(); // Use the Pinia store

// Computed property to check if the banner has been sent by the backend
let hasBannerBeenSentByBackend = computed(() => userDataStore.banners.some((banner) => banner.id === 'admin_acceptance'));

// bannerVisible controls the visibility of the cookie banner based on a local storage check
let bannerVisible = computed(() => !isBannerClosed('tech_admin_banner') && hasBannerBeenSentByBackend.value);

// Function is called when the user clicks the 'Accept' button
function acceptAdminPrivacyPolicy() {
  axiosLevelTech
    .post('api/menu/SetAdminAsAccepted')
    .then((response) => {
      // console.log('Server update successful:', response);
      closeBanner('tech_admin_banner');
    })
    .catch((error) => {
      console.error('Server update failed:', error);
    });
}

// Checks if the banner was closed before and calculates if it should reappear
function isBannerClosed(bannerId: string): boolean {
  const closedDateStr = localStorage.getItem(`bannerClosed-${bannerId}`);
  if (!closedDateStr) return false;

  const closedDate = new Date(closedDateStr);
  const reappearDate = new Date();
  reappearDate.setMonth(reappearDate.getMonth() - 12); // Customize the reappear logic as needed

  return closedDate > reappearDate;
}

// Saves the current date as the banner close date in localStorage
function closeBanner(bannerId: string) {
  const currentDate = new Date().toISOString();
  localStorage.setItem(`bannerClosed-${bannerId}`, currentDate);
}
</script>

<style lang="scss">
// Styling for the admin banner skeleton loader
.admin_banner_container {
  @include display_flex_center;

  // Styling for the admin banner card
  .admin_banner_card {
    @include display_flex_center;
    flex-direction: column;
    border-radius: 0px;
    border-bottom: 1px solid;
    border-color: $primary;
  }

  // Styling for the variable admin banner alert font colors
  .v-alert__content {
    .bg-warning {
      color: $site-purple;
    }
  }
}

// Styling and nested styling for the admin banner alert
.admin_banner_alert {
  max-width: 900px;
  gap: 10px;
  padding: 15px 0px;
  display: flex;

  // Remove right margin from the alert prepend icon
  .v-alert__prepend {
    margin-right: 0px;
    align-self: center;
  }

  // Display the alert content as a flex container on Desktop
  .v-alert__content {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    align-content: center;
    gap: 10px;
  }

  // Align the admin banner content in a column on mobile
  @media (max-width: $mobile-screen) {
    .v-alert__content {
      flex-direction: column;
      align-items: center;
    }
  }

  // Styling for the admin banner icon
  .v-icon {
    font-size: 25px !important;
    height: 25px !important;
    width: 25px !important;
  }

  // Remove margin from the banner buttons
  .v-btn {
    margin: 0px;
  }

  // Center the admin Banner Buttons inside a div
  .admin_banner_buttons {
    @include display_flex_center;
  }

  .mdi-alert {
    color: $primary;
    opacity: 1;
  }

  .mdi-close::before {
    content: '\F0159';
    color: $white;
  }
}

// Styling for the admin banner link
.admin_banner_link {
  color: $secondary;
}

// Align the admin banner content in a column on mobile
@media (max-width: $mobile-screen) {
  .admin_banner_alert {
    flex-direction: column;
    padding: 10px;
  }

  .v-alert__prepend {
    align-self: center;
  }
}
</style>
